"use client";

import * as React from "react";
import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { CaretDownIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { fromArrayValue, toArrayValue } from "../../lib/single-select-value";

/**
 * Migrated from `@radix-ui/react-accordion` to Base UI.
 *
 * The public API of every export here is unchanged, deliberately: 26 files
 * consume this component and the migration is worth nothing if it also has to
 * be a 26-file rewrite.
 *
 * This is the first component in the initiative where the two primitives
 * disagree about the *shape of the value* rather than the spelling of a prop.
 * Radix discriminates on `type="single" | "multiple"` and types `value`,
 * `defaultValue` and `onValueChange` around it — a bare string in single mode,
 * an array in multiple. Base UI dropped `type` for a `multiple` boolean and
 * made the value an array in **both** modes. All 16 call sites pass `type` and
 * eight of them pass a scalar, so `Accordion` below keeps Radix's discriminated
 * union and normalizes at the boundary. Nothing else in the app has to know.
 *
 * The rest of the differences:
 *
 *  1. **`Content` → `Panel`.** Base UI renamed the part. `AccordionContent`
 *     keeps its name and its `data-slot="accordion-content"`, so callers and the
 *     `[&_[data-slot=…]]` selectors that reach into it are unaffected.
 *  2. **`onValueChange` gains a second argument.** Base UI passes
 *     `eventDetails` alongside the value; forwarding it would widen the callback
 *     every caller is typed against, for a value none of them want.
 *  3. **The height animation reads a different variable.** See
 *     `AccordionContent`.
 *  4. **A disabled trigger is no longer natively disabled.** Base UI builds the
 *     trigger with `focusableWhenDisabled`, so a disabled item gets
 *     `aria-disabled` and `data-disabled` and stays reachable by keyboard,
 *     where Radix set the native `disabled` attribute and dropped it out of the
 *     tab order. The behaviour is an improvement and is kept, but Tailwind's
 *     `disabled:` variant compiles to `&:disabled` and so stopped matching —
 *     the whole accordion rendered at full strength with nothing to show it was
 *     disabled. `AccordionTrigger` carries both spellings now. The `disabled:`
 *     pair still earns its place for a caller who supplies their own natively
 *     disabled button through `render`.
 *
 * One difference is **not** absorbable, because it names a DOM attribute that
 * callers style through directly. Radix puts `data-state="open|closed"` on every
 * part; Base UI splits it — the item, header and panel carry `data-open`, and
 * the **trigger** carries `data-panel-open`, present when open with no closed
 * counterpart. One file styles through it (`PermissionGrid`) and is updated in
 * the same commit.
 */

/** Props shared by both modes — everything Base UI's root takes except the value. */
type AccordionRootProps = Omit<
  AccordionPrimitive.Root.Props<string>,
  "value" | "defaultValue" | "onValueChange" | "multiple" | "className"
> & { className?: string };

type AccordionSingleProps = AccordionRootProps & {
  type: "single";
  /**
   * Radix's flag for "the open item can be closed again", which its single mode
   * refuses to do without. Base UI has no equivalent — it always allows the
   * close — so `true` is the only value that can be honoured, and it is typed
   * that way rather than accepted and quietly ignored.
   *
   * Every one of the eight single-mode call sites already passes it, so this
   * costs nothing today and turns the one behaviour we cannot reproduce into a
   * compile error instead of a silent difference.
   */
  collapsible: true;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

type AccordionMultipleProps = AccordionRootProps & {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

function Accordion(props: AccordionProps) {
  if (props.type === "multiple") {
    // `type` is Radix's discriminator and has no Base UI counterpart; it is
    // destructured away so it cannot reach the DOM as an attribute.
    const { type: _type, value, defaultValue, onValueChange, ...rest } = props;

    return (
      <AccordionPrimitive.Root
        data-slot="accordion"
        multiple
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next)}
        {...rest}
      />
    );
  }

  // Both `type` and `collapsible` are Radix's spelling with no Base UI
  // counterpart — `multiple: false` is the default, and the close is always
  // allowed — so they are destructured away rather than reaching the DOM.
  const {
    type: _type,
    collapsible: _collapsible,
    value,
    defaultValue,
    onValueChange,
    ...rest
  } = props;

  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      value={toArrayValue(value)}
      defaultValue={toArrayValue(defaultValue)}
      onValueChange={(next) => onValueChange?.(fromArrayValue(next))}
      {...rest}
    />
  );
}

/**
 * `value` is optional in Base UI — an item without one falls back to its index.
 * Radix required it, and so does this: an index-keyed item silently changes
 * identity when the list around it is filtered, which is exactly what several
 * call sites do.
 */
type AccordionItemProps = Omit<
  AccordionPrimitive.Item.Props,
  "value" | "className"
> & {
  value: string;
  className?: string;
};

function AccordionItem({ className, ...props }: AccordionItemProps) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn("border-b last:border-b-0", className)}
      {...props}
    />
  );
}

type AccordionTriggerProps = Omit<
  AccordionPrimitive.Trigger.Props,
  "className"
> & { className?: string };

function AccordionTrigger({
  className,
  children,
  ...props
}: AccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&[data-panel-open]>svg]:rotate-180",
          className,
        )}
        {...props}
      >
        {children}
        <CaretDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

type AccordionContentProps = Omit<
  AccordionPrimitive.Panel.Props,
  "className"
> & { className?: string };

/**
 * The panel, and the one place a rename would have cost real behaviour rather
 * than a class name.
 *
 * The previous implementation used `animate-accordion-down` / `-up` from
 * `tw-animate-css`, whose keyframes interpolate height to
 * `var(--radix-accordion-content-height, …, auto)` — a variable **Radix** sets
 * on the panel. Base UI publishes `--accordion-panel-height` instead, so those
 * keyframes would have fallen through to their `auto` fallback, which does not
 * interpolate: the panel snaps to full height on the first frame. Nothing would
 * have failed — every screenshot in the baseline measures the settled state, and
 * the settled state is identical either way.
 *
 * So the keyframes are ours now (`packages/ui/src/styles.css`), reading Base
 * UI's variable, and the exit is keyed on `data-ending-style` — Base UI keeps
 * the panel mounted for exactly as long as that animation runs, then removes it.
 * `accordion.browser.test.tsx` scrubs the animation to prove the height really
 * interpolates.
 */
function AccordionContent({
  className,
  children,
  ...props
}: AccordionContentProps) {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="animate-accordion-panel-down data-[ending-style]:animate-accordion-panel-up overflow-hidden text-sm"
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitive.Panel>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
